import authService from './auth.service';
import type { SemanticCategory } from '../types/taskConfig.types';

const API_URL = '/api/v1';
const BASE = `${API_URL}/task-manager`;

/** Универсальный item — id всегда number, code/name могут быть null в edge cases бэкенда. */
export interface TaskManagerDictItem {
  id: number;
  code: string | null;
  name: string | null;
}

/** /task-statuses возвращает расширенный shape — global + per-org. */
export interface TaskStatusItem extends TaskManagerDictItem {
  organization_id: number | null;
  semantic_category: SemanticCategory | null;
  is_active: boolean | null;
}

/**
 * Dict endpoints под `/api/v1/task-manager/`:
 *   - /task-priorities
 *   - /dependency-types
 *   - /task-operation-types
 *   - /task-statuses
 *
 * Org-context везде через JWT claim `organization_id` (выдаётся login-v2 → select-organization).
 *
 * AssignmentRoles живут отдельно в `assignmentRoles.service.ts` (legacy reason — UI уже интегрирован).
 */
class TaskManagerDictsService {
  async listPriorities(): Promise<TaskManagerDictItem[]> {
    const r = await authService.fetchWithAuth(`${BASE}/task-priorities`);
    if (!r.ok) throw new Error(`Failed to list priorities: ${r.statusText}`);
    const data = await r.json();
    return Array.isArray(data) ? data : (data?.items ?? []);
  }

  async listDependencyTypes(): Promise<TaskManagerDictItem[]> {
    const r = await authService.fetchWithAuth(`${BASE}/dependency-types`);
    if (!r.ok) throw new Error(`Failed to list dependency types: ${r.statusText}`);
    const data = await r.json();
    return Array.isArray(data) ? data : (data?.items ?? []);
  }

  async listOperationTypes(): Promise<TaskManagerDictItem[]> {
    const r = await authService.fetchWithAuth(`${BASE}/task-operation-types`);
    if (!r.ok) throw new Error(`Failed to list operation types: ${r.statusText}`);
    const data = await r.json();
    return Array.isArray(data) ? data : (data?.items ?? []);
  }

  /**
   * /task-statuses — статусы текущей организации (резолвится по JWT claim `organization_id`).
   *
   * Легковеснее `GET /admin/org-configs/{id}` если нужны ТОЛЬКО статусы
   * (без transitions/field_definitions). Для workflow builder используем
   * admin endpoint, для простых дропдаунов задач — этот.
   */
  async listStatuses(): Promise<TaskStatusItem[]> {
    const r = await authService.fetchWithAuth(`${BASE}/task-statuses`);
    if (!r.ok) throw new Error(`Failed to list task statuses: ${r.statusText}`);
    const data = await r.json();
    return Array.isArray(data) ? data : (data?.items ?? []);
  }
}

export default new TaskManagerDictsService();
